import { UntitledIcon, type UntitledIconData } from '@faceup/icons'
import { ulAlertCircle } from '@faceup/icons/ulAlertCircle'
import { ulCheckCircle } from '@faceup/icons/ulCheckCircle'
import { ulInfoCircle } from '@faceup/icons/ulInfoCircle'
import { ulXCircle } from '@faceup/icons/ulXCircle'
import { ulXClose } from '@faceup/icons/ulXClose'
import { App, type NotificationArgsProps } from 'antd'

export const notificationTypes = ['basic', 'error', 'info', 'success', 'warning'] as const
export type NotificationType = (typeof notificationTypes)[number]

const icons: Record<NotificationType, { icon: UntitledIconData; color: string } | null> = {
  basic: null,
  info: { icon: ulInfoCircle, color: '#0085FF' },
  error: { icon: ulXCircle, color: '#F54070' },
  success: { icon: ulCheckCircle, color: '#32C27B' },
  warning: { icon: ulAlertCircle, color: '#FA914B' },
}

export type NotificationArgs = Omit<NotificationArgsProps, 'type' | 'icon' | 'closeIcon'>

export const useNotification = () => {
  const app = App.useApp()

  const getIcon = (type: NotificationType) => {
    const icon = icons[type]

    return icon ? <UntitledIcon icon={icon.icon} color={icon.color} size={24} /> : undefined
  }

  const createNotification = (
    type: NotificationType,
    { actions, message, ...props }: NotificationArgsProps
  ) => {
    return app.notification.open({
      ...props,
      icon: getIcon(type),
      message: actions ? <strong>{message}</strong> : message,
      actions,
      closeIcon: <UntitledIcon icon={ulXClose} size={22} color='#062D46' />,
    })
  }

  // return object with notification types as keys and functions that create notifications as values:
  return Object.fromEntries(
    notificationTypes.map(type => [
      type,
      (notificationArgs: NotificationArgs) => createNotification(type, notificationArgs),
    ])
  ) as Record<NotificationType, (args: NotificationArgs) => void>
}
